import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormControlLabel,
  Switch,
  Grid,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  Stack,
  Typography,
  useMediaQuery,
  Autocomplete,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { usersAPI, rolesAPI, companiesAPI } from "../services/api";
import { toast } from "react-toastify";

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  fullName: string;
  roles: string[];
  isActive: boolean;
  emailVerified: boolean;
  lastLoginAt?: string;
  createdAt: string;
  allowedCompanyIds: string[];
  allowedJurisdictions: string[];
}

interface Role {
  id: string;
  name: string;
  description?: string;
}

interface CompanyOption {
  id: string;
  name: string;
  jurisdiction?: string;
}

const createUserSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Required"),
  password: Yup.string()
    .min(12, "Min 12 characters")
    .matches(/[A-Z]/, "Must include an uppercase letter")
    .matches(/[a-z]/, "Must include a lowercase letter")
    .matches(/[0-9]/, "Must include a number")
    .matches(
      /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/,
      "Must include a special character",
    )
    .required("Required"),
  firstName: Yup.string(),
  lastName: Yup.string(),
});

const updateUserSchema = Yup.object().shape({
  firstName: Yup.string(),
  lastName: Yup.string(),
});

const AdminUsers: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error: any) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await rolesAPI.getAll();
      setRoles(response.data);
    } catch (error: any) {
      toast.error("Failed to load roles");
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await companiesAPI.getAll({ page: 1, limit: 5000 });
      const data = response.data?.data || response.data?.items || response.data || [];
      setCompanies(data);
    } catch (error: any) {
      toast.error("Failed to load companies");
    }
  };

  useEffect(() => {
    loadUsers();
    loadRoles();
    loadCompanies();
  }, []);

  const handleCreate = () => {
    setEditingUser(null);
    setOpenDialog(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setOpenDialog(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this user?")) {
      return;
    }

    try {
      await usersAPI.delete(id);
      toast.success("User deleted successfully");
      loadUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to delete user");
    }
  };

  const syncUserRoles = async (
    userId: string,
    currentRoles: string[],
    desiredRoles: string[],
  ) => {
    const current = new Set(currentRoles || []);
    const desired = new Set((desiredRoles || []).filter(Boolean));
    if (desired.size === 0) {
      desired.add("USER");
    }

    for (const role of desired) {
      if (!current.has(role)) {
        await usersAPI.assignRole(userId, role);
      }
    }

    for (const role of current) {
      if (!desired.has(role)) {
        await usersAPI.removeRole(userId, role);
      }
    }
  };

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      const {
        allowedCompanyIds = [],
        allowedJurisdictions = [],
        roles = [],
        ...userPayload
      } = values;
      const desiredRoles: string[] = Array.from(new Set((roles as string[]).filter(Boolean)));

      if (editingUser) {
        // Update existing user
        await usersAPI.update(editingUser.id, userPayload);
        await syncUserRoles(editingUser.id, editingUser.roles || [], desiredRoles);
        await usersAPI.updatePermissions(
          editingUser.id,
          allowedCompanyIds,
          allowedJurisdictions,
        );
        toast.success("User updated successfully");
      } else {
        // Create new user
        const created = await usersAPI.create(userPayload);
        if (created.data?.id) {
          await syncUserRoles(created.data.id, ["USER"], desiredRoles);
          await usersAPI.updatePermissions(
            created.data.id,
            allowedCompanyIds,
            allowedJurisdictions,
          );
        }
        toast.success("User created successfully");
      }
      setOpenDialog(false);
      loadUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      await usersAPI.update(user.id, { isActive: !user.isActive });
      toast.success(`User ${!user.isActive ? "activated" : "deactivated"}`);
      loadUsers();
    } catch (error: any) {
      toast.error("Failed to update user status");
    }
  };

  const columns: GridColDef[] = [
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "fullName",
      headerName: "Name",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "roles",
      headerName: "Roles",
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<User>) => (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {params.row.roles.map((role: string) => (
            <Chip
              key={role}
              label={role}
              size="small"
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
      ),
    },
    {
      field: "isActive",
      headerName: "Status",
      width: 120,
      renderCell: (params: GridRenderCellParams<User>) => (
        <Chip
          label={params.row.isActive ? "Active" : "Inactive"}
          color={params.row.isActive ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "emailVerified",
      headerName: "Email",
      width: 100,
      renderCell: (params: GridRenderCellParams<User>) => (
        <Chip
          label={params.row.emailVerified ? "Verified" : "Unverified"}
          color={params.row.emailVerified ? "success" : "warning"}
          size="small"
        />
      ),
    },
    {
      field: "createdAt",
      headerName: "Created",
      width: 120,
      valueFormatter: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams<User>) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              aria-label="Edit user"
              size="small"
              onClick={() => handleEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.isActive ? "Deactivate" : "Activate"}>
            <IconButton
              aria-label={
                params.row.isActive ? "Deactivate user" : "Activate user"
              }
              size="small"
              onClick={() => handleToggleActive(params.row)}
            >
              <Switch checked={params.row.isActive} size="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              aria-label="Delete user"
              size="small"
              onClick={() => handleDelete(params.row.id)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const initialValues = editingUser
    ? {
        firstName: editingUser.firstName || "",
        lastName: editingUser.lastName || "",
        roles: editingUser.roles,
        isActive: editingUser.isActive,
        allowedCompanyIds: editingUser.allowedCompanyIds || [],
        allowedJurisdictions: editingUser.allowedJurisdictions || [],
      }
    : {
        email: "",
        password: "",
        firstName: "",
        lastName: "",
        roles: ["USER"],
        isActive: true,
        allowedCompanyIds: [],
        allowedJurisdictions: [],
      };

  const renderMobileCards = () => (
    <Stack spacing={2}>
      {users.map((user) => (
        <Card key={user.id} sx={{ borderRadius: 2 }}>
          <CardContent sx={{ pb: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 1,
                mb: 1.5,
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} noWrap>
                  {user.fullName || user.email}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {user.email}
                </Typography>
              </Box>
              <Chip
                label={user.isActive ? "Active" : "Inactive"}
                color={user.isActive ? "success" : "default"}
                size="small"
              />
            </Box>

            <Stack spacing={1}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {user.roles.map((role) => (
                  <Chip
                    key={role}
                    label={role}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Email:
                </Typography>
                <Chip
                  label={user.emailVerified ? "Verified" : "Unverified"}
                  color={user.emailVerified ? "success" : "warning"}
                  size="small"
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Created {new Date(user.createdAt).toLocaleDateString()}
              </Typography>
            </Stack>
          </CardContent>
          <CardActions
            sx={{
              px: 2,
              pb: 2,
              pt: 0,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => handleEdit(user)}
              startIcon={<EditIcon />}
              fullWidth
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color={user.isActive ? "warning" : "success"}
              onClick={() => handleToggleActive(user)}
              fullWidth
            >
              {user.isActive ? "Deactivate" : "Activate"}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => handleDelete(user.id)}
              startIcon={<DeleteIcon />}
              sx={{ gridColumn: "1 / -1" }}
              fullWidth
            >
              Delete
            </Button>
          </CardActions>
        </Card>
      ))}
    </Stack>
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Create User
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={loadUsers}>
          Refresh
        </Button>
      </Box>

      {isMobile ? (
        loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading users…
          </Typography>
        ) : users.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No users found.
          </Typography>
        ) : (
          renderMobileCards()
        )
      ) : (
        <Box sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={users}
            columns={columns}
            loading={loading}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            disableRowSelectionOnClick
          />
        </Box>
      )}

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={isMobile ? { px: 2, py: 1.5 } : undefined}>
          {editingUser ? "Edit User" : "Create User"}
        </DialogTitle>
        <Formik
          initialValues={initialValues}
          validationSchema={editingUser ? updateUserSchema : createUserSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent sx={isMobile ? { px: 2 } : undefined}>
                <Grid container spacing={2}>
                  {!editingUser && (
                    <>
                      <Grid item xs={12}>
                        <Field name="email">
                          {({ field }: any) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Email *"
                              type="email"
                              required
                              error={touched.email && Boolean(errors.email)}
                              helperText={touched.email && errors.email}
                            />
                          )}
                        </Field>
                      </Grid>
                      <Grid item xs={12}>
                        <Field name="password">
                          {({ field }: any) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Password *"
                              type="password"
                              required
                              error={
                                touched.password && Boolean(errors.password)
                              }
                              helperText={touched.password && errors.password}
                            />
                          )}
                        </Field>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} sm={6}>
                    <Field name="firstName">
                      {({ field }: any) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="First Name"
                          error={touched.firstName && Boolean(errors.firstName)}
                          helperText={touched.firstName && errors.firstName}
                        />
                      )}
                    </Field>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Field name="lastName">
                      {({ field }: any) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Last Name"
                          error={touched.lastName && Boolean(errors.lastName)}
                          helperText={touched.lastName && errors.lastName}
                        />
                      )}
                    </Field>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Roles</InputLabel>
                      <Select
                        multiple
                        value={values.roles}
                        onChange={(e) => setFieldValue("roles", e.target.value)}
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {selected.map((value: string) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {roles.map((role) => (
                          <MenuItem key={role.id} value={role.name}>
                            {role.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={companies}
                      value={companies.filter((company) =>
                        (((values as any).allowedCompanyIds as string[] | undefined) || []).includes(
                          company.id,
                        ),
                      )}
                      getOptionLabel={(option) =>
                        option.jurisdiction
                          ? `${option.name} (${option.jurisdiction})`
                          : option.name
                      }
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      onChange={(_, selected) =>
                        setFieldValue(
                          "allowedCompanyIds",
                          selected.map((company) => company.id),
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Allowed Companies"
                          placeholder="Select companies for scoped access"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isActive}
                          onChange={(e) =>
                            setFieldValue("isActive", e.target.checked)
                          }
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions
                sx={{
                  ...(isMobile && {
                    flexDirection: "column",
                    gap: 1,
                    px: 2,
                    pb: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
                    "& > .MuiButton-root": { width: "100%", minHeight: 44 },
                  }),
                }}
              >
                <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Saving..."
                    : editingUser
                      ? "Update"
                      : "Create"}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Box>
  );
};

export default AdminUsers;
